/**
 * FlightUnsubscribeExportPanel — operator download of the flight's
 * recipient-level unsubscribe CSV.
 *
 * The file (email addresses + scope/group + timestamps) is served ONLY by the
 * JWT + management-role gated `export-flight-unsubscribes` edge function; the
 * browser never fetches raw addresses to build it. This panel:
 *   - is visible only to flight-management roles (admin / superadmin / manager);
 *   - loads a count (no addresses) to drive the disabled zero-state + metric;
 *   - shows loading / success / actionable-failure states on download;
 *   - explains global vs category/group unsubscribe and the historical group
 *     coverage boundary.
 */
import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  downloadFlightUnsubscribesCsv,
  getFlightUnsubscribeSummary,
  type FlightUnsubscribeSummary,
} from "../../services/campaigns.service";

// Mirrors the server's FLIGHT_MGMT_ROLES (admin / superadmin / manager).
const EXPORT_ROLES = new Set(["admin", "superadmin", "manager"]);

interface Props {
  flightId: string;
}

export default function FlightUnsubscribeExportPanel({ flightId }: Props) {
  const { user } = useAuth();
  const role = (user?.role ?? "").toString().toLowerCase();
  const canExport = EXPORT_ROLES.has(role);

  const [summary, setSummary] = useState<FlightUnsubscribeSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadSuccess, setDownloadSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!canExport) {
      return;
    }
    let cancelled = false;
    setLoadingSummary(true);
    setSummaryError(null);
    getFlightUnsubscribeSummary(flightId)
      .then((s) => {
        if (!cancelled) {
          setSummary(s);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setSummaryError(
            e instanceof Error ? e.message : "Failed to load unsubscribe count",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingSummary(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [flightId, canExport]);

  if (!canExport) {
    return null;
  }

  const count = summary?.operational_rows ?? 0;
  const distinct = summary?.distinct_recipients ?? 0;
  const hasEvents = count > 0;

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    setDownloadSuccess(null);
    try {
      const res = await downloadFlightUnsubscribesCsv(flightId);
      setDownloadSuccess(
        `Downloaded ${res.rowCount.toLocaleString()} row${res.rowCount === 1 ? "" : "s"} (${res.distinctRecipients.toLocaleString()} recipient${res.distinctRecipients === 1 ? "" : "s"}).`,
      );
    } catch (e: unknown) {
      setDownloadError(e instanceof Error ? e.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="px-6 pb-6 pt-2 border-t border-gray-100">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900">
              Unsubscribes
            </div>
            <p className="mt-1 text-sm text-gray-600">
              Download the recipients who unsubscribed on this flight to update
              external systems. Includes <strong>global</strong> unsubscribes
              (all email) and <strong>category/group</strong> unsubscribes, with
              first and latest event timestamps and current status. One row per
              recipient and scope.
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Note: durable category/group history is captured going forward
              from the latest release; group unsubscribes from before then are
              not included yet. Global unsubscribes are backfilled.
            </p>
            {loadingSummary ? (
              <p className="mt-2 text-sm text-gray-500">
                Checking for unsubscribes…
              </p>
            ) : summaryError ? (
              <p className="mt-2 text-sm text-red-600">{summaryError}</p>
            ) : (
              <p className="mt-2 text-sm text-gray-700">
                {hasEvents
                  ? `${count.toLocaleString()} unsubscribe row${count === 1 ? "" : "s"} (${distinct.toLocaleString()} recipient${distinct === 1 ? "" : "s"}).`
                  : "No unsubscribes recorded for this flight yet."}
              </p>
            )}
          </div>
          <div className="shrink-0">
            <button
              type="button"
              onClick={handleDownload}
              disabled={!hasEvents || downloading || loadingSummary}
              title={
                !hasEvents
                  ? "No unsubscribes recorded for this flight yet"
                  : "Download the unsubscribes CSV"
              }
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
            >
              {downloading ? "Preparing…" : "Download unsubscribes CSV"}
            </button>
          </div>
        </div>
        {downloadError && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {downloadError}{" "}
            <button
              type="button"
              onClick={handleDownload}
              className="font-medium underline hover:no-underline"
            >
              Try again
            </button>
          </div>
        )}
        {downloadSuccess && (
          <div className="mt-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
            {downloadSuccess}
          </div>
        )}
      </div>
    </div>
  );
}
